import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Home, LogOut, ChevronDown, KeyRound, Loader2 } from "lucide-react";

export function AgentHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const doLogout = async () => { await logout(); navigate("/login", { replace: true }); };

  const changePw = async () => {
    if (nw.length < 8) return toast.error("New password must be at least 8 characters");
    if (nw !== confirm) return toast.error("New passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password updated");
      setPwOpen(false); setCur(""); setNw(""); setConfirm("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't change password"); }
    setBusy(false);
  };

  return (
    <header className="h-16 border-b border-border bg-card/70 backdrop-blur sticky top-0 z-30 flex items-center px-4 sm:px-8">
      <Link to="/agent" className="flex items-center gap-2">
        <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center"><Home className="h-4 w-4 text-primary-foreground" /></div>
        <span className="font-heading text-lg font-bold">Look Before You Leap 808</span>
        <span className="hidden sm:inline text-xs font-semibold bg-forest/10 text-forest px-2 py-0.5 rounded-full ml-1">Agent</span>
      </Link>
      <Link to="/agent/vendors" className="ml-5 hidden sm:inline text-sm font-medium text-muted-foreground hover:text-foreground transition-colors" data-testid="agent-nav-vendors">Vendor Manager</Link>
      <div className="ml-auto flex items-center gap-3">
        <ThemeToggle />
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-2 rounded-full hover:bg-secondary px-1.5 py-1 transition-colors outline-none" data-testid="agent-user-menu">
            <Avatar className="h-8 w-8"><AvatarImage src={user?.picture} /><AvatarFallback className="bg-forest text-white text-xs">{user?.name?.slice(0, 2).toUpperCase()}</AvatarFallback></Avatar>
            <span className="hidden sm:block text-sm font-medium">{user?.name}</span>
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setPwOpen(true)} data-testid="agent-change-pw-item" className="gap-2 cursor-pointer"><KeyRound className="h-4 w-4" /> Change password</DropdownMenuItem>
            <DropdownMenuItem onClick={doLogout} data-testid="agent-logout-btn" className="text-destructive gap-2 cursor-pointer"><LogOut className="h-4 w-4" /> Log out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Change password</DialogTitle>
            <DialogDescription>Enter your current password, then choose a new one (at least 8 characters).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Current password</Label><Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="pw-current" className="h-11 rounded-xl" /></div>
            <div className="space-y-1.5"><Label>New password</Label><Input type="password" value={nw} onChange={(e) => setNw(e.target.value)} data-testid="pw-new" className="h-11 rounded-xl" /></div>
            <div className="space-y-1.5"><Label>Confirm new password</Label><Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="pw-confirm" className="h-11 rounded-xl" /></div>
          </div>
          <DialogFooter>
            <Button onClick={changePw} disabled={busy || !cur || !nw} data-testid="pw-save-btn" className="rounded-full gap-2">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Update password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </header>
  );
}
